import routes from "routes";

type TextToken = {
  type: "text";
  value: string;
};

type VariableToken = {
  type: "variable";
  name: string;
  prefix?: string;
};

type RouteToken = TextToken | VariableToken;

type RouteDefinition = {
  tokens: RouteToken[];
  defaults?: Record<string, string | number>;
};

type Routes = Record<string, RouteDefinition>;

export type RouteName = keyof typeof routes;

type RouteParams = Record<string, string | number>;
type QueryParams = Record<string, string | number>;

const typedRoutes = routes as Routes;

export type { RouteDefinition, RouteToken, RouteParams, QueryParams };

export const routing = {
  getRoute(routeName: RouteName): RouteDefinition {
    const route = typedRoutes[routeName];

    if (!route) {
      throw new Error(`Route "${routeName}" not found`);
    }

    return route;
  },

  getVariableNames(routeName: RouteName): string[] {
    const route = this.getRoute(routeName);

    return route.tokens
      .filter((token) => token.type === "variable")
      .map((token) => (token as VariableToken).name);
  },

  generate(
    routeName: RouteName,
    params: RouteParams = {},
    query: QueryParams = {},
  ): string {
    const route = this.getRoute(routeName);

    if (!route) {
      throw new Error(`Route "${routeName}" not found`);
    }

    const defaults = route.defaults ?? {};
    const mutableParams = { ...params };
    let path = "";

    for (const token of route.tokens) {
      if (token.type === "text") {
        path += token.value;
        continue;
      }

      if (token.type === "variable") {
        const tokenName = token.name;

        let value: string | number | undefined = mutableParams[tokenName];

        if (value == null && defaults[tokenName] != null) {
          value = defaults[tokenName];
        }

        if (value == null) {
          throw new Error(
            `Missing parameter "${tokenName}" for route "${routeName}"`,
          );
        }

        path += (token.prefix ?? "") + encodeURIComponent(String(value));
        delete mutableParams[tokenName];
      }
    }

    const qs = new URLSearchParams(query as Record<string, string>).toString();
    if (qs) {
      path += `?${qs}`;
    }

    return path;
  },
};
